import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const formatClockTime = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec < 10 ? "0" : ""}${sec}`;
};

// Recording URLs are served by a JWT-protected Flask endpoint. Native <audio>
// can't send the Authorization header, so we fetch with auth and feed a blob
// URL to the element. Tokens live under portal-specific keys in localStorage.
const getAuthToken = () => {
  try {
    const currentUser = localStorage.getItem("current_user");
    const keys = ["admin", "reseller", currentUser ? `user_${currentUser}` : null].filter(Boolean);
    for (const key of keys) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed?.access_token) return parsed.access_token;
    }
  } catch {
    /* localStorage unavailable or JSON parse failed */
  }
  return null;
};

const fetchAudioBlob = async (src) => {
  const token = getAuthToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(src, { headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.blob();
};

function RecordingCell({ src, audioControls }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleRefCallback = useCallback(
    (el) => {
      audioRef.current = el;
      if (el && audioControls?.registerAudioRef) {
        audioControls.registerAudioRef(src, el);
      }
    },
    [src, audioControls]
  );

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  const ensureBlobSrc = useCallback(async () => {
    if (blobUrl) return blobUrl;
    const blob = await fetchAudioBlob(src);
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    if (audioRef.current) audioRef.current.src = url;
    return url;
  }, [blobUrl, src]);

  const togglePlay = async () => {
    if (!audioRef.current) return;
    try {
      if (!blobUrl) {
        setLoading(true);
        await ensureBlobSrc();
        setLoading(false);
      }
      if (audioControls?.handleAudioClick) {
        audioControls.handleAudioClick(src);
      } else if (isPlaying) {
        audioRef.current.pause();
      } else {
        await audioRef.current.play();
      }
    } catch (e) {
      setLoading(false);
      // eslint-disable-next-line no-console
      console.error("Recording play failed:", e);
    }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (!src) return;
    try {
      const blob = await fetchAudioBlob(src);
      const url = URL.createObjectURL(blob);
      const fileName = src.split("/").pop()?.split("?")[0] || "recording";
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName.endsWith(".wav") ? fileName : `${fileName}.wav`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Recording download failed:", err);
    }
  };

  if (!src) {
    return <span style={{ opacity: 0.5 }}>—</span>;
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <audio
        ref={handleRefCallback}
        preload="none"
        onPlay={() => setIsPlaying(true)}
        onPause={() => {
          setIsPlaying(false);
          if (audioControls?.handleAudioPause) audioControls.handleAudioPause();
        }}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />
      <IconButton
        size="small"
        onClick={togglePlay}
        disabled={loading}
        aria-label={isPlaying ? "Pause recording" : "Play recording"}
        sx={{ p: 0.5, color: "#f5751d" }}
      >
        {loading ? (
          <CircularProgress size={16} sx={{ color: "#f5751d" }} />
        ) : isPlaying ? (
          <PauseIcon fontSize="small" />
        ) : (
          <PlayArrowIcon fontSize="small" />
        )}
      </IconButton>
      <Typography
        variant="caption"
        sx={{
          fontFamily: "monospace",
          fontSize: "0.75rem",
          minWidth: "32px",
          letterSpacing: "0.3px",
        }}
      >
        {formatClockTime(isPlaying ? currentTime : duration)}
      </Typography>
      <Tooltip title="Download recording" arrow>
        <IconButton
          size="small"
          onClick={handleDownload}
          aria-label="Download recording"
          sx={{ p: 0.5, color: "#1976d2" }}
        >
          <FileDownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default RecordingCell;
